import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Building2, 
  Users, 
  Target, 
  CreditCard, 
  Settings, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  RefreshCw,
  Save,
  Edit2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export function TenantDetailPage() {
  const { isSuperAdmin, loading: authLoading } = useSuperAdmin();
  const navigate = useNavigate();
  const { tenantId } = useParams();
  
  const [tenant, setTenant] = useState(null);
  const [usage, setUsage] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [actualCounts, setActualCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Edit states
  const [editingEntitlements, setEditingEntitlements] = useState(false);
  const [entitlementsForm, setEntitlementsForm] = useState({});
  
  // Modal states
  const [showGrantLeadsModal, setShowGrantLeadsModal] = useState(false);
  const [leadsToGrant, setLeadsToGrant] = useState('1000');
  
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  
  useEffect(() => {
    if (isSuperAdmin && tenantId) {
      fetchTenantDetails();
    }
  }, [isSuperAdmin, tenantId]);

  const fetchTenantDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/saas-admin/tenants/${tenantId}`,
        { withCredentials: true }
      );
      
      setTenant(response.data.tenant);
      setUsage(response.data.usage);
      setEntitlements(response.data.entitlements);
      setAdminUser(response.data.admin_user);
      setActualCounts(response.data.actual_counts);
      
      // Initialize entitlements form
      if (response.data.entitlements) {
        setEntitlementsForm({
          max_users_included: response.data.entitlements.max_users_included,
          max_leads_included: response.data.entitlements.max_leads_included,
          extra_user_price_eur: response.data.entitlements.extra_user_price_eur,
          lead_topup_price_eur: response.data.entitlements.lead_topup_price_eur,
          ai_intro_mail_enabled: response.data.entitlements.ai_intro_mail_enabled
        });
      }
    } catch (error) {
      console.error('Error fetching tenant details:', error);
      toast.error('Failed to load tenant details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEntitlements = async () => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/saas-admin/tenants/${tenantId}/entitlements`,
        entitlementsForm,
        { withCredentials: true }
      );
      
      toast.success('Entitlements updated successfully');
      setEditingEntitlements(false);
      fetchTenantDetails();
    } catch (error) {
      console.error('Error updating entitlements:', error);
      toast.error('Failed to update entitlements');
    }
  };

  const handleToggleAIIntroMail = async (enabled) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/saas-admin/tenants/${tenantId}/ai-intro-mail`,
        { enabled },
        { withCredentials: true }
      );
      
      toast.success(`AI Intro Mail ${enabled ? 'enabled' : 'disabled'}`);
      fetchTenantDetails();
    } catch (error) {
      console.error('Error toggling AI intro mail:', error);
      toast.error('Failed to update AI intro mail');
    }
  };

  const handleGrantLeads = async () => {
    try {
      const leads = parseInt(leadsToGrant);
      if (isNaN(leads) || leads <= 0) {
        toast.error('Please enter a valid number of leads');
        return;
      }
      
      await axios.post(
        `${BACKEND_URL}/api/saas-admin/tenants/${tenantId}/grant-leads`,
        { leads_to_add: leads },
        { withCredentials: true }
      );
      
      toast.success(`Granted ${leads} extra leads successfully`);
      setShowGrantLeadsModal(false);
      setLeadsToGrant('1000');
      fetchTenantDetails();
    } catch (error) {
      console.error('Error granting leads:', error);
      toast.error('Failed to grant leads');
    }
  };

  const handleChangePlan = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }
    
    try {
      await axios.patch(
        `${BACKEND_URL}/api/saas-admin/tenants/${tenantId}`,
        { plan_key: selectedPlan },
        { withCredentials: true }
      );
      
      toast.success(`Plan changed to ${selectedPlan.toUpperCase()} successfully`);
      setShowChangePlanModal(false);
      setSelectedPlan('');
      fetchTenantDetails();
    } catch (error) {
      console.error('Error changing plan:', error);
      toast.error('Failed to change plan');
    }
  };

  const handleSuspendTenant = async () => {
    if (!window.confirm('Are you sure you want to suspend this tenant?')) {
      return;
    }
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/saas-admin/tenants/${tenantId}/suspend`,
        {},
        { withCredentials: true }
      );
      
      toast.success('Tenant suspended successfully');
      fetchTenantDetails();
    } catch (error) {
      console.error('Error suspending tenant:', error);
      toast.error('Failed to suspend tenant');
    }
  };

  const handleActivateTenant = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/saas-admin/tenants/${tenantId}/activate`,
        {},
        { withCredentials: true }
      );
      
      toast.success('Tenant activated successfully');
      fetchTenantDetails();
    } catch (error) {
      console.error('Error activating tenant:', error);
      toast.error('Failed to activate tenant');
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking access...</p>
        </div>
      </div>
    );
  }

  // Redirect if not SuperAdmin
  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tenant details...</p>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Tenant Not Found</h2>
          <button
            onClick={() => navigate('/saas-admin/tenants')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Tenants
          </button>
        </div>
      </div>
    );
  }

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'trialing': return 'bg-blue-100 text-blue-800';
      case 'suspended': return 'bg-orange-100 text-orange-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'base': return 'bg-gray-100 text-gray-800';
      case 'tier1': return 'bg-blue-100 text-blue-800';
      case 'tier2': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/saas-admin/tenants')}
                className="p-2 hover:bg-gray-100 rounded-md"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{tenant.name}</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Tenant ID: {tenant.id}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full capitalize ${getStatusBadgeColor(tenant.status)}`}>
                {tenant.status}
              </span>
              <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full uppercase ${getPlanBadgeColor(tenant.plan_key)}`}>
                {tenant.plan_key}
              </span>
              <button
                onClick={fetchTenantDetails}
                className="p-2 hover:bg-gray-100 rounded-md"
              >
                <RefreshCw className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Info & Usage */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tenant Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Building2 className="w-5 h-5 mr-2" />
                Tenant Information
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Tenant Name</p>
                  <p className="text-sm font-medium text-gray-900">{tenant.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created At</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(tenant.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Primary Admin</p>
                  <p className="text-sm font-medium text-gray-900">
                    {adminUser ? `${adminUser.email}` : 'Not set'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${getStatusBadgeColor(tenant.status)}`}>
                    {tenant.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Plan & Entitlements */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  Plan & Entitlements
                </h2>
                {!editingEntitlements && (
                  <button
                    onClick={() => setEditingEntitlements(true)}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                )}
              </div>

              {entitlements && (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Current Plan</p>
                    <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full uppercase ${getPlanBadgeColor(tenant.plan_key)}`}>
                      {tenant.plan_key} - €{entitlements.monthly_price_eur}/mo
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Max Users</p>
                      {editingEntitlements ? (
                        <input
                          type="number"
                          value={entitlementsForm.max_users_included}
                          onChange={(e) => setEntitlementsForm({
                            ...entitlementsForm,
                            max_users_included: parseInt(e.target.value)
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="text-lg font-medium text-gray-900">{entitlements.max_users_included}</p>
                      )}
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-1">Max Leads</p>
                      {editingEntitlements ? (
                        <input
                          type="number"
                          value={entitlementsForm.max_leads_included}
                          onChange={(e) => setEntitlementsForm({
                            ...entitlementsForm,
                            max_leads_included: parseInt(e.target.value)
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="text-lg font-medium text-gray-900">{entitlements.max_leads_included}</p>
                      )}
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-1">Extra User Price</p>
                      {editingEntitlements ? (
                        <input
                          type="number"
                          step="0.01"
                          value={entitlementsForm.extra_user_price_eur}
                          onChange={(e) => setEntitlementsForm({
                            ...entitlementsForm,
                            extra_user_price_eur: parseFloat(e.target.value)
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="text-lg font-medium text-gray-900">€{entitlements.extra_user_price_eur}</p>
                      )}
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-1">Lead Topup Price</p>
                      {editingEntitlements ? (
                        <input
                          type="number"
                          step="0.01"
                          value={entitlementsForm.lead_topup_price_eur}
                          onChange={(e) => setEntitlementsForm({
                            ...entitlementsForm,
                            lead_topup_price_eur: parseFloat(e.target.value)
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      ) : (
                        <p className="text-lg font-medium text-gray-900">€{entitlements.lead_topup_price_eur}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 mb-2">AI Intro Mail</p>
                    {editingEntitlements ? (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={entitlementsForm.ai_intro_mail_enabled}
                          onChange={(e) => setEntitlementsForm({
                            ...entitlementsForm,
                            ai_intro_mail_enabled: e.target.checked
                          })}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="ml-2 text-sm text-gray-900">Enabled</span>
                      </label>
                    ) : (
                      <div className="flex items-center">
                        {entitlements.ai_intro_mail_enabled ? (
                          <>
                            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                            <span className="text-sm font-medium text-green-600">Enabled</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-5 h-5 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-400">Disabled</span>
                          </>
                        )}
                        <button
                          onClick={() => handleToggleAIIntroMail(!entitlements.ai_intro_mail_enabled)}
                          className="ml-4 text-xs text-blue-600 hover:text-blue-800"
                        >
                          Toggle
                        </button>
                      </div>
                    )}
                  </div>

                  {editingEntitlements && (
                    <div className="flex gap-2 pt-4 border-t">
                      <button
                        onClick={handleSaveEntitlements}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        <Save className="w-4 h-4" />
                        Save Changes
                      </button>
                      <button
                        onClick={() => {
                          setEditingEntitlements(false);
                          setEntitlementsForm({
                            max_users_included: entitlements.max_users_included,
                            max_leads_included: entitlements.max_leads_included,
                            extra_user_price_eur: entitlements.extra_user_price_eur,
                            lead_topup_price_eur: entitlements.lead_topup_price_eur,
                            ai_intro_mail_enabled: entitlements.ai_intro_mail_enabled
                          });
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Usage */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Current Usage
              </h2>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-500">Active Users</span>
                    <span className="text-sm font-medium text-gray-900">
                      {usage?.users_active_count || 0} / {entitlements?.max_users_included || 0}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{
                        width: `${Math.min(100, ((usage?.users_active_count || 0) / (entitlements?.max_users_included || 1)) * 100)}%`
                      }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-500">Total Leads</span>
                    <span className="text-sm font-medium text-gray-900">
                      {usage?.leads_total_count || 0} / {entitlements?.max_leads_included || 0}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{
                        width: `${Math.min(100, ((usage?.leads_total_count || 0) / (entitlements?.max_leads_included || 1)) * 100)}%`
                      }}
                    ></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-sm text-gray-500">AI Emails Sent</p>
                    <p className="text-lg font-medium text-gray-900">{usage?.ai_emails_sent || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Lead Topups Purchased</p>
                    <p className="text-lg font-medium text-gray-900">{usage?.leads_topups_purchased || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Billing */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CreditCard className="w-5 h-5 mr-2" />
                Billing Information
              </h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Stripe Customer ID</p>
                  {tenant.stripe_customer_id ? (
                    <a
                      href={`https://dashboard.stripe.com/customers/${tenant.stripe_customer_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      {tenant.stripe_customer_id}
                    </a>
                  ) : (
                    <p className="text-sm text-gray-400">Not set</p>
                  )}
                </div>

                <div>
                  <p className="text-sm text-gray-500">Stripe Subscription ID</p>
                  {tenant.stripe_subscription_id ? (
                    <a
                      href={`https://dashboard.stripe.com/subscriptions/${tenant.stripe_subscription_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      {tenant.stripe_subscription_id}
                    </a>
                  ) : (
                    <p className="text-sm text-gray-400">Not set</p>
                  )}
                </div>

                <div>
                  <p className="text-sm text-gray-500">Subscription Status</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">
                    {tenant.subscription_status || 'Not subscribed'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Actions */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Target className="w-5 h-5 mr-2" />
                Actions
              </h2>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setSelectedPlan(tenant.plan_key);
                    setShowChangePlanModal(true);
                  }}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center"
                >
                  Change Plan
                </button>

                <button
                  onClick={() => setShowGrantLeadsModal(true)}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center"
                >
                  Grant Extra Leads
                </button>

                <button
                  onClick={() => navigate(`/saas-admin/tenants/${tenantId}/api-management`)}
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center justify-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  API Management
                </button>

                {tenant.status === 'active' ? (
                  <button
                    onClick={handleSuspendTenant}
                    className="w-full px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 flex items-center justify-center"
                  >
                    Suspend Tenant
                  </button>
                ) : (
                  <button
                    onClick={handleActivateTenant}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center"
                  >
                    Activate Tenant
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grant Leads Modal */}
      {showGrantLeadsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Grant Extra Leads</h3>
            <p className="text-sm text-gray-600 mb-4">
              Enter the number of additional leads to grant to this tenant.
            </p>
            <input
              type="number"
              value={leadsToGrant}
              onChange={(e) => setLeadsToGrant(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
              placeholder="e.g., 1000"
            />
            <div className="flex gap-2">
              <button
                onClick={handleGrantLeads}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Grant Leads
              </button>
              <button
                onClick={() => {
                  setShowGrantLeadsModal(false);
                  setLeadsToGrant('1000');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {showChangePlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Plan</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select a new plan for this tenant. Entitlements will be automatically updated.
            </p>
            <div className="space-y-2 mb-4">
              {['base', 'tier1', 'tier2'].map((plan) => (
                <label key={plan} className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="plan"
                    value={plan}
                    checked={selectedPlan === plan}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className={`ml-3 px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full uppercase ${getPlanBadgeColor(plan)}`}>
                    {plan}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleChangePlan}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Change Plan
              </button>
              <button
                onClick={() => {
                  setShowChangePlanModal(false);
                  setSelectedPlan('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
